import { useAuth } from '../context/Context';
import { canModuleBeDeleted, isSuperAdminRole } from '../config/permissions';

/**
 * RBAC hook. Reads the flat `permissions` map returned by signin / get-profile.
 *
 * Contract:
 *   - Super Admin is decided by role NAME, never by the permissions map — the
 *     map comes back sparse (or empty) for Super Admin because the backend
 *     bypasses permission checks for that role entirely.
 *   - A missing module key or action key is `false`.
 *   - `role: null` (no role assigned) means zero permissions.
 *   - `edit` covers create + update. `canCreate` is an alias for `canEdit`.
 *   - `delete` only exists on the five Content sub-modules; asking for it
 *     anywhere else always returns false.
 */
export const usePermission = () => {
    const { user } = useAuth();

    const role = user?.role ?? null;
    const isSuperAdmin = isSuperAdminRole(role);

    const can = (module, action = 'view') => {
        if (!module) return false;

        // Super Admin sees and does everything, regardless of the map.
        if (isSuperAdmin) return true;

        // No role assigned (or profile not loaded yet) → no access.
        if (!role) return false;

        // Delete is defined only for the Content sub-modules.
        if (action === 'delete' && !canModuleBeDeleted(module)) return false;

        // Only view / edit / delete / publish exist. Every write-flavoured action a
        // screen asks for (create, block, reply, assign, send, …) resolves to
        // `edit` — one Edit toggle governs all of them. `publish` is the one
        // deliberate exception (bulk_series_import), which has its own permission
        // distinct from edit — see cinema-admin-service's src/lib/permissions.js.
        const resolved = action === 'view' || action === 'delete' || action === 'publish' ? action : 'edit';

        return user?.permissions?.[module]?.[resolved] === true;
    };

    const canView = (module) => can(module, 'view');
    const canEdit = (module) => can(module, 'edit');
    const canCreate = (module) => can(module, 'edit');
    const canDelete = (module) => can(module, 'delete');
    const canPublish = (module) => can(module, 'publish');

    return {
        can,
        canView,
        canCreate,
        canEdit,
        canDelete,
        canPublish,
        isSuperAdmin,
        permissions: user?.permissions || null,
        role,
        roleName: typeof role === 'string' ? role : role?.name || null,
        permissionsVersion: user?.permissions_version ?? null,
    };
};
